import {AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {AnonymousBookingVerifyResponse, AnonymousEventVerifyResponse, calculateEventDiscount, EventBookingDTO, EventBookingOption, EventCommitteeMember, EventDTO, EventFormAnswer, EventFormQuestion, EventParticipantDTO, EventRegistrationDTO, EventService, EventSpeaker, EventTicketOption, isDiscountActive, isStripePaymentEnabled, QuickSignupRequest} from '../api/event-api.service';
import {missingRequiredLabels, pruneAnswers} from '../registration-questions/registration-questions.util';
import {buildFeeLines, FeeCategory, FeeLine, getUnitFee, sumFeeLines} from '../event-fee-breakdown';
import {SpinnerService} from '../shared/services/spinner.service';
import {PaymentApiService} from '../api/payment-api.service';
import {finalize} from 'rxjs';
import {Swiper} from 'swiper';
import {SwiperOptions} from 'swiper/types';

// Letters, spaces, hyphens and apostrophes only (covers names like "Mary-Jane" or "O'Brien"),
// after trimming — blocks digits/symbols and whitespace-only input in one pass.
function nameValidator(control: AbstractControl): ValidationErrors | null {
    const value = typeof control.value === 'string' ? control.value.trim() : '';
    if (!value) {
        return {required: true};
    }
    return /^[A-Za-z][A-Za-z '-]*$/.test(value) ? null : {invalidName: true};
}

// Letters, spaces, periods, hyphens and apostrophes only, after trimming — for place names
// like "St. Louis" or "Winston-Salem". Rejects digits-only input (e.g. someone typing a zip
// code into City) and blank/whitespace-only input.
function placeNameValidator(control: AbstractControl): ValidationErrors | null {
    const value = typeof control.value === 'string' ? control.value.trim() : '';
    if (!value) {
        return {required: true};
    }
    return /^[A-Za-z][A-Za-z .'-]*$/.test(value) ? null : {invalidName: true};
}

// Digits plus common phone punctuation (+, -, (, ), space) only, after trimming, with at
// least 7 digits — rejects letters (e.g. someone typing gibberish into Phone) and symbol-only
// input that would otherwise pass a bare non-blank check.
function phoneValidator(control: AbstractControl): ValidationErrors | null {
    const value = typeof control.value === 'string' ? control.value.trim() : '';
    if (!value) {
        return {required: true};
    }
    if (!/^[0-9+\-() ]+$/.test(value)) {
        return {invalidPhone: true};
    }
    const digitCount = (value.match(/\d/g) || []).length;
    return digitCount >= 7 ? null : {invalidPhone: true};
}

@Component({
    selector: 'app-register-event',
    templateUrl: './register-event.component.html',
    styleUrl: './register-event.component.css'
})
export class RegisterEventComponent implements OnInit, OnDestroy, AfterViewChecked {

    @Input() externalEventId?: string;
    get embedded(): boolean { return !!this.externalEventId; }
    eventId: string = '';
    eventTitle: string = '';
    eventStatus: string = '';
    event: EventDTO | null = null;
    verifiedUserId: string = '';
    verifiedUserName: string = '';
    step: 'register' | 'readonly' | 'confirmation' = 'register';
    errorMessage: string = '';
    registrationSummary: string = '';
    existingRegistration: EventRegistrationDTO | null = null;
    existingBooking: EventBookingDTO | null = null;
    isPaymentCompleted: boolean = false;
    verifiedEmail: string = '';
    emailVerifying: boolean = false;
    showQuickSignup: boolean = false;
    quickSignupForm: FormGroup;

    // CONFERENCE booking state
    bookingQuantities: { [optionId: string]: number } = {};
    bookingDonationAmt: number = 0;
    bookingAttending: 'YES' | 'NO' | '' = '';
    // Answers to the organizer-defined registration questions (CONFERENCE booking / LOCAL registration).
    bookingFormAnswers: EventFormAnswer[] = [];
    localFormAnswers: EventFormAnswer[] = [];
    localSubmitAttempted = false;
    bookingPaymentType: string = 'later';
    bookingPaymentReference: string = '';
    bookingConfirmationSummary: string = '';
    bookingSubmitAttempted: boolean = false;

    registrationForm: FormGroup;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder,
        private eventService: EventService,
        private toastr: ToastrService,
        private spinner: SpinnerService,
        private paymentApi: PaymentApiService
    ) {
        this.registrationForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            adults: [0],
            children: [0],
            seniors: [0],
            childAges: [''],
            donationAmt: [0],
            paymentType: ['later', Validators.required],
            paymentReference: ['']
        });
        this.quickSignupForm = this.fb.group({
            firstName: ['', nameValidator],
            lastName: ['', nameValidator],
            phone: ['', phoneValidator],
            state: ['', placeNameValidator],
            city: ['', placeNameValidator]
        });
    }

    ngOnInit(): void {
        this.eventId = this.externalEventId || this.route.snapshot.paramMap.get('id') || '';

        // Standalone only: logged-in users go to event-home (embedded parent handles this)
        if (!this.embedded && sessionStorage.getItem('username')) {
            this.router.navigate(['/event-home', this.eventId]);
            return;
        }

        this.loadPublicEventDetails();

        // Clear verification state when user edits the email after verifying
        this.registrationForm.get('email')?.valueChanges.subscribe((val: string) => {
            const normalized = String(val || '').trim().toLowerCase();
            if (this.verifiedEmail && normalized !== this.verifiedEmail.toLowerCase()) {
                this.clearVerificationState();
                this.resetRegistrationFields();
            }
        });
    }

    ngOnDestroy(): void {
        this.committeeResizeObserver?.disconnect();
        this.guestResizeObserver?.disconnect();
        this.speakerResizeObserver?.disconnect();
        this.committeeSwiper?.destroy();
        this.guestSwiper?.destroy();
        this.speakerSwiper?.destroy();
    }

    // Re-syncs the committee/guest/speaker carousels' slide widths after every change-detection pass.
    // See event-home.component.ts for the full rationale — a fixed delay or ResizeObserver alone
    // isn't reliably enough to catch every layout shift that happens before Swiper's initial measurement.
    ngAfterViewChecked(): void {
        this.committeeSwiper?.update();
        this.guestSwiper?.update();
        this.speakerSwiper?.update();
    }

    private clearVerificationState(): void {
        this.verifiedEmail = '';
        this.verifiedUserId = '';
        this.verifiedUserName = '';
        this.existingRegistration = null;
        this.existingBooking = null;
        this.isPaymentCompleted = false;
        this.errorMessage = '';
        this.showQuickSignup = false;
        this.bookingSubmitAttempted = false;
        this.localSubmitAttempted = false;
    }

    private resetRegistrationFields(): void {
        // Clear all registration fields but preserve the email the user typed
        this.registrationForm.patchValue({
            adults: 0,
            children: 0,
            seniors: 0,
            childAges: '',
            donationAmt: 0,
            paymentType: 'later',
            paymentReference: ''
        }, {emitEvent: false});
    }

    loadPublicEventDetails(): void {
        this.spinner.show();
        this.eventService.getPublicEventDetails(this.eventId).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (eventData) => {
                this.eventTitle = eventData.title;
                this.eventStatus = eventData.status || '';
                this.event = eventData;
                this.buildGroupedTicketOptions();
                this.loadParticipantsForDisplay();
            },
            error: () => {
                this.toastr.error('Event not found');
                this.eventTitle = '';
            }
        });
    }

    // -------------------- Event Committee / Guests of Honor / Speakers --------------------
    // Public read-only display, sourced from the EventParticipant table (userId already stripped
    // server-side) rather than the deprecated embedded eventCommittee/eventGuestsOfHonor/eventSpeakers
    // fields on EventDTO.
    displayEventCommittee: EventCommitteeMember[] = [];
    displayEventGuestsOfHonor: EventCommitteeMember[] = [];
    displayEventSpeakers: EventSpeaker[] = [];

    private loadParticipantsForDisplay(): void {
        this.eventService.getPublicEventParticipants(this.eventId).subscribe({
            next: (participants) => {
                this.displayEventCommittee = participants
                    .filter(p => p.participantType === 'COMMITTEE')
                    .map(p => this.toDisplayMember(p));
                this.displayEventGuestsOfHonor = participants
                    .filter(p => p.participantType === 'GUEST_OF_HONOR')
                    .map(p => this.toDisplayMember(p));
                this.displayEventSpeakers = participants
                    .filter(p => p.participantType === 'SPEAKER')
                    .map(p => this.toDisplaySpeaker(p));
            },
            error: () => {
            }
        });
    }

    private toDisplayMember(p: EventParticipantDTO): EventCommitteeMember {
        return {
            memberId: p.userId,
            name: p.name,
            title: p.title,
            shortDescription: p.shortDescription,
            photoUrl: p.photoUrl,
            videoUrl: p.videoUrl
        };
    }

    private toDisplaySpeaker(p: EventParticipantDTO): EventSpeaker {
        return {
            speakerId: p.id,
            name: p.name,
            role: p.title,
            company: p.company,
            topic: p.topic,
            topicDescription: p.topicDescription,
            tags: p.tags,
            photoUrl: p.photoUrl,
            videoUrl: p.videoUrl
        };
    }

    // ---- Computed getters (same logic as EventHomeComponent) ----

    get isPaidEvent(): boolean {
        return (this.event?.eventType || '').toLowerCase() === 'paid';
    }

    get hasChildAgeLimitRule(): boolean {
        return this.isPaidEvent && this.getChildAgeMax() !== null;
    }

    get childrenCountLabel(): string {
        const minAge = this.getChildAgeMin();
        const maxAge = this.getChildAgeMax();
        if (this.hasChildAgeLimitRule && maxAge !== null) {
            return `Children (Ages ${minAge} to ${maxAge})`;
        }
        return 'Children';
    }

    get childAgesPlaceholder(): string {
        const minAge = this.getChildAgeMin();
        const maxAge = this.getChildAgeMax();
        if (this.hasChildAgeLimitRule && maxAge !== null) {
            return `Example: ${minAge},${minAge + 2},${maxAge}`;
        }
        return 'Example: 6,8,10';
    }

    get selectedPaymentType(): 'stripe' | 'zelle' | 'check' | 'later' {
        const raw = String(this.registrationForm.get('paymentType')?.value || 'later').toLowerCase();
        if (raw === 'zelle' || raw === 'check' || raw === 'later') {
            return raw;
        }
        return this.isStripeEnabled() ? 'stripe' : 'later';
    }

    isStripeEnabled(): boolean {
        return isStripePaymentEnabled(this.event);
    }

    // A saved 'stripe' choice falls back to 'later' once card payment is switched off for the event,
    // so the form never pre-selects a radio that is hidden.
    private availablePaymentType(paymentType: string | undefined): string {
        const normalized = String(paymentType || 'later').toLowerCase();
        return normalized === 'stripe' && !this.isStripeEnabled() ? 'later' : normalized;
    }

    get isOfflinePayment(): boolean {
        return this.selectedPaymentType === 'zelle' || this.selectedPaymentType === 'check';
    }

    get paymentReferenceLabel(): string {
        return this.selectedPaymentType === 'check' ? 'Check Number' : 'Zelle Confirmation Number';
    }

    get paymentSubmitLabel(): string {
        return this.selectedPaymentType === 'stripe'
            ? 'Submit & Pay with Card'
            : 'Submit Registration';
    }

    get bookingSubmitLabel(): string {
        return this.bookingPaymentType === 'stripe'
            ? 'Submit & Pay with Card'
            : 'Submit Registration';
    }

    private getChildAgeMax(): number | null {
        const raw = this.event?.childAgeLimit;
        if (raw == null || raw === '') return null;
        const value = Number(raw);
        return Number.isFinite(value) && value >= 0 ? value : null;
    }

    private getChildAgeMin(): number {
        const raw = this.event?.childAgeMin;
        if (raw == null || raw === '') return 5;
        const value = Number(raw);
        return (!Number.isFinite(value) || value < 0) ? 5 : Math.floor(value);
    }

    private getCount(controlName: 'adults' | 'children' | 'seniors'): number {
        const value = Number(this.registrationForm.get(controlName)?.value ?? 0);
        return (!Number.isFinite(value)) ? 0 : Math.max(0, Math.floor(value));
    }

    // Public — the "Amount Due" breakdown template reads this directly.
    getDonationAmount(): number {
        const value = Number(this.registrationForm.get('donationAmt')?.value ?? 0);
        return (!Number.isFinite(value)) ? 0 : Math.max(0, value);
    }

    // Per-category count x unit fee lines for the paid-event breakdown. getFeesSubtotal() sums these
    // same lines, so the breakdown can never disagree with Amount Due.
    get feeLines(): FeeLine[] {
        return buildFeeLines(
            this.event,
            {adults: this.getCount('adults'), children: this.getCount('children'), seniors: this.getCount('seniors')},
            {adults: 'Adults', children: this.childrenCountLabel, seniors: 'Senior Citizens'}
        );
    }

    // Per-person fee for a category (0 when none) — shown next to the count input labels.
    unitFee(category: FeeCategory): number {
        return getUnitFee(this.event, category);
    }

    trackFeeLine(_index: number, line: FeeLine): string {
        return line.category;
    }

    // Public — the registration-fees breakdown template reads this directly.
    getFeesSubtotal(): number {
        return sumFeeLines(this.feeLines);
    }

    // Early bird discount currently applicable to the fees subtotal (0 when no active discount).
    // Never applies to the donation amount.
    getDiscountAmount(): number {
        return calculateEventDiscount(this.event, this.getFeesSubtotal());
    }

    // True only while the event's discount date window is open — NOT tied to any particular
    // subtotal. Templates should additionally check getDiscountAmount()/getBookingDiscountAmount()
    // > 0 for the specific total they're displaying (fees vs. booking options can be zero
    // independently of each other, e.g. a CONFERENCE event has no per-person fees at all).
    isDiscountActive(): boolean {
        return isDiscountActive(this.event);
    }

    // True when the event has a configured, positive discount value — independent of the date
    // window (isDiscountActive()) and of any specific subtotal (getDiscountAmount()/
    // getBookingDiscountAmount()). Used to gate the informational "Early Bird Discount: ..."
    // note so it doesn't show "$0.00 off" once an admin zeroes out the value but leaves the
    // dates in place.
    hasDiscountValue(): boolean {
        const value = parseFloat(String(this.event?.discountValue ?? ''));
        return Number.isFinite(value) && value > 0;
    }

    // Renders the "YYYY-MM-DD" discountEndDate as "20th Sep" for the discount call-to-action text.
    formatDiscountEndDate(dateStr: string | null | undefined): string {
        if (!dateStr) return '';
        const parts = dateStr.split('-').map(Number);
        if (parts.length !== 3) return dateStr;
        const [year, month, day] = parts;
        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime())) return dateStr;
        const monthShort = date.toLocaleString('en-US', {month: 'short'});
        const suffix = (day > 3 && day < 21) ? 'th'
            : (day % 10 === 1) ? 'st'
            : (day % 10 === 2) ? 'nd'
            : (day % 10 === 3) ? 'rd' : 'th';
        return `${day}${suffix} ${monthShort}`;
    }

    calculateAmountDue(): number {
        const donation = this.getDonationAmount();
        const feesSubtotal = this.getFeesSubtotal();
        const discount = calculateEventDiscount(this.event, feesSubtotal);
        return feesSubtotal - discount + donation;
    }

    formatTime(time: string): string {
        if (!time) return '';
        const [h, m] = time.split(':').map(Number);
        const period = h >= 12 ? 'PM' : 'AM';
        const hour = h % 12 || 12;
        return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
    }

    onEmailBlur(): void {
        // Shared by the input's (blur) and the tap-to-verify search icon's (click) — mobile users
        // often don't realize tapping outside the field is what triggers verification, so the icon
        // gives them an explicit action. Tapping the icon also blurs the input first, so both
        // handlers can fire for the same intent; emailVerifying guards against sending it twice.
        if (this.emailVerifying) return;

        const emailCtrl = this.registrationForm.get('email');
        if (!emailCtrl || emailCtrl.invalid) return;

        const email = String(emailCtrl.value || '').trim();
        if (!email) return;

        // Skip if already verified for this email
        if (this.verifiedEmail && this.verifiedEmail.toLowerCase() === email.toLowerCase()) {
            return;
        }

        this.verifyEmail(email);
    }

    private verifyEmail(email: string, onVerified?: () => void): void {
        this.errorMessage = '';
        this.emailVerifying = true;
        this.spinner.show();

        if (this.isConferenceEvent) {
            this.eventService.verifyEmailForEventBooking(this.eventId, email).pipe(
                finalize(() => { this.emailVerifying = false; this.spinner.hide(); })
            ).subscribe({
                next: (response: AnonymousBookingVerifyResponse) => {
                    if (!response.valid) {
                        this.clearVerificationState();
                        this.errorMessage = 'Email not found. You can sign up below to continue with registration.';
                        this.showQuickSignup = true;
                        return;
                    }
                    this.verifiedEmail = email;
                    this.verifiedUserId = response.userId;
                    this.verifiedUserName = response.spouseName
                        ? `${response.firstName} & ${response.spouseName}`
                        : (response.firstName || '');
                    this.event = response.event || this.event;
                    this.existingBooking = response.existingBooking || null;
                    if (this.existingBooking?.status === 'PAYMENT_CONFIRMED') {
                        this.isPaymentCompleted = true;
                        this.step = 'readonly';
                        return;
                    }
                    if (this.existingBooking) {
                        this.prefillBookingForm(this.existingBooking);
                    }
                    if (onVerified) onVerified();
                },
                error: () => {
                    this.clearVerificationState();
                    this.errorMessage = 'Unable to verify email. Please try again.';
                }
            });
        } else {
            this.eventService.verifyEmailForEvent(this.eventId, email).pipe(
                finalize(() => { this.emailVerifying = false; this.spinner.hide(); })
            ).subscribe({
                next: (response: AnonymousEventVerifyResponse) => {
                    if (!response.valid) {
                        this.clearVerificationState();
                        this.resetRegistrationFields();
                        this.errorMessage = 'Email not found. You can sign up below to continue with registration.';
                        this.showQuickSignup = true;
                        return;
                    }
                    this.verifiedEmail = email;
                    this.verifiedUserId = response.userId;
                    this.verifiedUserName = response.spouseName
                        ? `${response.firstName} & ${response.spouseName}`
                        : (response.firstName || '');
                    this.event = response.event || this.event;
                    this.existingRegistration = response.existingRegistration || null;
                    this.localFormAnswers = this.existingRegistration?.formAnswers ? JSON.parse(JSON.stringify(this.existingRegistration.formAnswers)) : [];
                    if (this.existingRegistration && this.existingRegistration.status === 'PAYMENT_CONFIRMED') {
                        this.isPaymentCompleted = true;
                        this.step = 'readonly';
                        return;
                    }
                    if (this.existingRegistration) {
                        this.prefillRegistrationForm(this.existingRegistration);
                    }
                    if (onVerified) onVerified();
                },
                error: () => {
                    this.clearVerificationState();
                    this.resetRegistrationFields();
                    this.errorMessage = 'Unable to verify email. Please try again.';
                }
            });
        }
    }

    submitQuickSignup(): void {
        if (!this.quickSignupForm.valid) {
            this.quickSignupForm.markAllAsTouched();
            return;
        }
        const email = String(this.registrationForm.get('email')?.value || '').trim();
        if (!email) {
            this.toastr.error('Please enter an email address first');
            return;
        }
        const request: QuickSignupRequest = {
            email,
            firstName: this.quickSignupForm.get('firstName')?.value?.trim(),
            lastName: this.quickSignupForm.get('lastName')?.value?.trim(),
            phone: this.quickSignupForm.get('phone')?.value?.trim(),
            state: this.quickSignupForm.get('state')?.value?.trim(),
            city: this.quickSignupForm.get('city')?.value?.trim()
        };

        this.spinner.show();
        this.eventService.quickSignupForEvent(this.eventId, request).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (response) => {
                this.verifiedEmail = email;
                this.verifiedUserId = response.userId;
                this.verifiedUserName = response.firstName + (response.lastName ? ' ' + response.lastName : '');
                this.showQuickSignup = false;
                this.errorMessage = '';
                this.toastr.success('Sign up successful! Please continue with your registration.');
            },
            error: (err) => {
                this.toastr.error(err?.error?.message || 'Sign up failed. Please try again.');
            }
        });
    }

    submitRegistration(): void {
        if (!this.registrationForm.valid) {
            this.registrationForm.markAllAsTouched();
            return;
        }

        const missingAnswers = missingRequiredLabels(this.registrationQuestions, this.localFormAnswers);
        if (missingAnswers.length > 0) {
            this.localSubmitAttempted = true;
            this.toastr.warning(`Please answer: ${missingAnswers.join(', ')}`);
            return;
        }

        const email = String(this.registrationForm.get('email')?.value || '').trim();

        // If already verified for this email, save directly
        if (this.verifiedEmail && this.verifiedEmail.toLowerCase() === email.toLowerCase() && this.verifiedUserId) {
            this.saveRegistration();
            return;
        }

        // Otherwise verify first, then save on success
        this.verifyEmail(email, () => this.saveRegistration());
    }

    private saveRegistration(): void {
        const paymentType = this.selectedPaymentType;
        const paymentReference = String(this.registrationForm.get('paymentReference')?.value || '').trim();

        const adults = this.getCount('adults');
        const children = this.getCount('children');
        const seniors = this.getCount('seniors');
        const donation = this.getDonationAmount();

        const registration = {
            userId: this.verifiedUserId,
            adults,
            children,
            seniors,
            childAges: String(this.registrationForm.get('childAges')?.value || '').trim(),
            donationAmt: donation,
            paymentType,
            paymentReference: paymentType === 'stripe' ? '' : paymentReference,
            status: 'PAYMENT_PENDING',
            formAnswers: pruneAnswers(this.registrationQuestions, this.localFormAnswers)
        };

        this.spinner.show();
        this.eventService.anonymousEventRegistration(this.eventId, registration).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: () => {
                if (paymentType === 'stripe') {
                    this.toastr.success('Registration saved. Redirecting to Stripe...');
                    // Stripe redirects off-site and reloads the app on return, so this
                    // in-memory registration is lost unless persisted here — the payment
                    // success page needs it (plus the verified user id) to confirm this
                    // same registration through the public endpoint afterward.
                    sessionStorage.setItem('eventRegistration', JSON.stringify({
                        ...registration,
                        status: 'PAYMENT_CONFIRMED'
                    }));
                    this.payment(this.event?.title || '', this.calculateAmountDue());
                    return;
                }

                this.registrationSummary = this.buildSummary(adults, children, seniors, donation, paymentType);
                this.step = 'confirmation';
                this.toastr.success('Registration saved successfully!');
            },
            error: (err) => {
                this.toastr.error('Error registering: ' + (err?.error?.message || err?.message || 'Please try again'));
            }
        });
    }

    private prefillRegistrationForm(reg: EventRegistrationDTO): void {
        // Preserve the email the user typed — only patch registration fields
        this.registrationForm.patchValue({
            adults: Number(reg.adults) || 0,
            children: Number(reg.children) || 0,
            seniors: Number(reg.seniors) || 0,
            childAges: reg.childAges || '',
            donationAmt: Number(reg.donationAmt) || 0,
            paymentType: this.availablePaymentType(reg.paymentType),
            paymentReference: reg.paymentReference || ''
        }, {emitEvent: false});
    }

    private payment(eventName: string, amount: number): void {
        if (!this.isStripeEnabled()) {
            this.toastr.error('Card payments are not enabled for this event');
            return;
        }
        // The server prices the checkout from the registration it just saved; the browser only says who is paying.
        const stripeRequest = {
            eventId: this.eventId,
            userId: this.verifiedUserId
        };

        this.paymentApi.createEventCheckoutSession(stripeRequest).subscribe({
            next: (res) => {
                sessionStorage.setItem('eventId', this.eventId);
                sessionStorage.setItem('eventReturnPath', `/event-register/${this.eventId}`);
                const stripe = (window as any).Stripe(res.publicKey);
                stripe.redirectToCheckout({sessionId: res.sessionId});
            },
            error: (err) => this.toastr.error(err.error?.error || err.error || 'Stripe checkout failed', 'Error')
        });
    }

    getPaymentStatusLabel(): string {
        if (!this.existingRegistration) return '';
        switch (this.existingRegistration.status) {
            case 'PAYMENT_CONFIRMED': return 'Payment Confirmed';
            case 'PAYMENT_PENDING': return 'Payment Pending';
            case 'REGISTRATION_PENDING': return 'Registration Pending';
            default: return this.existingRegistration.status || '';
        }
    }

    getPaymentTypeLabel(type?: string): string {
        switch (type) {
            case 'stripe': return 'Card (Stripe)';
            case 'zelle': return 'Zelle';
            case 'check': return 'Check';
            case 'later': return 'Pay Later';
            default: return type || '';
        }
    }

    // -------------------- CONFERENCE Booking --------------------

    get isConferenceEvent(): boolean {
        return this.event?.eventCategory === 'CONFERENCE';
    }

    get allTicketOptions(): EventTicketOption[] {
        return this.event?.ticketOptions || [];
    }

    groupedTicketOptions: { sectionLabel: string; options: EventTicketOption[] }[] = [];

    private buildGroupedTicketOptions(): void {
        const map = new Map<string, EventTicketOption[]>();
        const order: string[] = [];
        for (const opt of this.allTicketOptions) {
            const key = opt.sectionLabel || '';
            if (!map.has(key)) { map.set(key, []); order.push(key); }
            map.get(key)!.push(opt);
        }
        this.groupedTicketOptions = order.map(label => ({sectionLabel: label, options: map.get(label)!}));
    }

    getBookingOptionLabel(optionId: string): string {
        return this.event?.ticketOptions?.find(o => o.optionId === optionId)?.label || optionId;
    }

    get hasSelectedPaidBookingOption(): boolean {
        return this.allTicketOptions.some(opt =>
            opt.paid !== false && (this.bookingQuantities[opt.optionId!] || 0) > 0);
    }

    get showBookingOptionsError(): boolean {
        return this.bookingSubmitAttempted && !this.hasSelectedPaidBookingOption;
    }

    get registrationQuestions(): EventFormQuestion[] {
        return this.event?.registrationQuestions || [];
    }

    // CONFERENCE events created before organizer-defined questions keep the built-in Yes/No attending radio.
    get usesLegacyAttending(): boolean {
        return this.isConferenceEvent && this.event?.registrationQuestions == null;
    }

    get showBookingAttendingError(): boolean {
        return this.bookingSubmitAttempted && !this.bookingAttending;
    }

    getBookingOptionsSummary(booking: EventBookingDTO): string {
        if (booking.selectedOptions?.length) {
            return booking.selectedOptions
                .filter(o => o.quantity > 0)
                .map(o => `${this.getBookingOptionLabel(o.optionId)} ×${o.quantity}`)
                .join(', ');
        }
        return this.getBookingOptionLabel(booking.ticketOptionId || '');
    }

    // Public — the booking breakdown template reads this directly.
    getBookingOptionsSubtotal(): number {
        return this.allTicketOptions.reduce((sum, opt) => {
            const qty = this.bookingQuantities[opt.optionId!] || 0;
            const price = opt.paid === false ? 0 : (parseFloat(opt.price) || 0);
            return sum + price * qty;
        }, 0);
    }

    // Early bird discount currently applicable to the booking's ticket-option subtotal
    // (0 when no active discount). Never applies to the donation amount.
    getBookingDiscountAmount(): number {
        return calculateEventDiscount(this.event, this.getBookingOptionsSubtotal());
    }

    calculateBookingTotal(): number {
        const optionTotal = this.getBookingOptionsSubtotal();
        const discount = calculateEventDiscount(this.event, optionTotal);
        return optionTotal - discount + (this.bookingDonationAmt || 0);
    }

    private prefillBookingForm(booking: EventBookingDTO): void {
        this.bookingQuantities = {};
        if (booking.selectedOptions?.length) {
            booking.selectedOptions.forEach(o => {
                this.bookingQuantities[o.optionId] = o.quantity;
            });
        }
        this.bookingDonationAmt = booking.donationAmt || 0;
        this.bookingAttending = booking.attending || '';
        this.bookingFormAnswers = booking.formAnswers ? JSON.parse(JSON.stringify(booking.formAnswers)) : [];
        this.bookingPaymentType = this.availablePaymentType(booking.paymentType);
        this.bookingPaymentReference = booking.paymentReference || '';
    }

    private getMissingRequiredGroups(options: EventTicketOption[], quantities: { [optionId: string]: number }): string[] {
        const groupLabels = new Set(
            options.filter(o => o.groupRequired).map(o => o.sectionLabel || '')
        );
        return Array.from(groupLabels).filter(label => {
            const groupOptions = options.filter(o => (o.sectionLabel || '') === label);
            return !groupOptions.some(o => (quantities[o.optionId!] || 0) > 0);
        });
    }

    submitBooking(): void {
        const missingRequired = this.allTicketOptions.filter(opt =>
            opt.required && !(this.bookingQuantities[opt.optionId!] > 0));
        if (missingRequired.length > 0) {
            this.toastr.warning(`Please enter a quantity for required option(s): ${missingRequired.map(o => o.label).join(', ')}`);
            return;
        }

        const missingRequiredGroups = this.getMissingRequiredGroups(this.allTicketOptions, this.bookingQuantities);
        if (missingRequiredGroups.length > 0) {
            this.toastr.warning(`Please select at least one option in: ${missingRequiredGroups.join(', ')}`);
            return;
        }

        if (!this.hasSelectedPaidBookingOption) {
            this.bookingSubmitAttempted = true;
            this.toastr.warning('Please select at least one of the option.');
            return;
        }

        if (this.usesLegacyAttending && !this.bookingAttending) {
            this.bookingSubmitAttempted = true;
            this.toastr.warning('Please select whether you are attending the conference.');
            return;
        }

        const missingAnswers = this.usesLegacyAttending
            ? []
            : missingRequiredLabels(this.registrationQuestions, this.bookingFormAnswers);
        if (missingAnswers.length > 0) {
            this.bookingSubmitAttempted = true;
            this.toastr.warning(`Please answer: ${missingAnswers.join(', ')}`);
            return;
        }

        const email = String(this.registrationForm.get('email')?.value || '').trim();
        if (this.verifiedEmail && this.verifiedEmail.toLowerCase() === email.toLowerCase() && this.verifiedUserId) {
            this.saveBooking();
            return;
        }
        this.verifyEmail(email, () => this.saveBooking());
    }

    private saveBooking(): void {
        const selectedOptions: EventBookingOption[] = Object.entries(this.bookingQuantities)
            .filter(([, qty]) => qty > 0)
            .map(([optionId, quantity]) => ({optionId, quantity}));
        const dto: EventBookingDTO = {
            eventId: this.eventId,
            userId: this.verifiedUserId,
            selectedOptions,
            donationAmt: this.bookingDonationAmt,
            paymentType: this.bookingPaymentType as any,
            paymentReference: this.bookingPaymentType === 'stripe' ? '' : this.bookingPaymentReference,
            status: 'PAYMENT_PENDING',
            registrationSource: 'PUBLIC',
            attending: this.usesLegacyAttending ? this.bookingAttending as 'YES' | 'NO' : undefined,
            formAnswers: this.usesLegacyAttending ? undefined : pruneAnswers(this.registrationQuestions, this.bookingFormAnswers)
        };
        this.spinner.show();
        this.eventService.createPublicEventBooking(this.eventId, dto).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (booking) => {
                this.existingBooking = booking;
                const total = booking.totalAmount || this.calculateBookingTotal();
                if (this.bookingPaymentType === 'stripe') {
                    this.toastr.success('Registration saved. Redirecting to Stripe...');
                    // CONFERENCE bookings live in a separate table from LOCAL
                    // EventRegistrations — keyed under 'eventBooking' so payment-success
                    // can tell which kind of record it's confirming after Stripe returns.
                    sessionStorage.setItem('eventBooking', JSON.stringify({
                        ...dto,
                        status: 'PAYMENT_CONFIRMED'
                    }));
                    this.payment(this.event?.title || '', total);
                    return;
                }
                const optionSummary = selectedOptions
                    .map(o => `${this.getBookingOptionLabel(o.optionId)} ×${o.quantity}`)
                    .join(', ');
                this.bookingConfirmationSummary = `${optionSummary} | Total: $${total.toFixed(2)} | Payment: ${this.getPaymentTypeLabel(booking.paymentType)}`;
                this.step = 'confirmation';
                this.toastr.success('Booking submitted successfully!');
            },
            error: (err) => {
                this.toastr.error('Error submitting booking: ' + (err?.error?.message || 'Please try again'));
            }
        });
    }

    private buildSummary(adults: number, children: number, seniors: number, donation: number, paymentType: string): string {
        const parts: string[] = [];
        if (adults > 0) parts.push(`${adults} Adult(s)`);
        if (children > 0) parts.push(`${children} Child(ren)`);
        if (seniors > 0) parts.push(`${seniors} Senior(s)`);
        if (donation > 0) parts.push(`Donation: $${donation}`);

        const paymentLabel = paymentType === 'stripe' ? 'Card (Stripe)'
            : paymentType === 'zelle' ? 'Zelle'
            : paymentType === 'check' ? 'Check'
            : 'Pay Later';
        parts.push(`Payment: ${paymentLabel}`);

        if (this.isPaidEvent) {
            parts.push(`Amount Due: $${this.calculateAmountDue().toFixed(2)}`);
        }

        return parts.join(' | ');
    }

    // -------------------- Speakers carousel --------------------
    private speakerSwiper: Swiper | null = null;
    private speakerResizeObserver: ResizeObserver | null = null;

    private committeeSwiper: Swiper | null = null;
    private committeeResizeObserver: ResizeObserver | null = null;

    @ViewChild('committeeSwiperContainer') set committeeSwiperContainer(el: ElementRef<HTMLDivElement> | undefined) {
        if (el && !this.committeeSwiper) {
            setTimeout(() => {
                this.committeeSwiper = new Swiper(el.nativeElement, this.getCommitteeSwiperOptions());
                this.committeeResizeObserver = new ResizeObserver(() => this.committeeSwiper?.update());
                this.committeeResizeObserver.observe(el.nativeElement);
            });
        }
    }

    private getCommitteeSwiperOptions(): SwiperOptions {
        const memberCount = this.displayEventCommittee.length;
        return {
            loop: memberCount > 3,
            observer: true,
            observeParents: true,
            spaceBetween: 20,
            speed: 1500,
            autoplay: {
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            },
            breakpoints: {
                0: {slidesPerView: 1, spaceBetween: 10},
                768: {slidesPerView: 2, spaceBetween: 20},
                992: {slidesPerView: 3, spaceBetween: 20}
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true
            }
        };
    }

    private guestSwiper: Swiper | null = null;
    private guestResizeObserver: ResizeObserver | null = null;

    @ViewChild('guestSwiperContainer') set guestSwiperContainer(el: ElementRef<HTMLDivElement> | undefined) {
        if (el && !this.guestSwiper) {
            setTimeout(() => {
                this.guestSwiper = new Swiper(el.nativeElement, this.getGuestSwiperOptions());
                this.guestResizeObserver = new ResizeObserver(() => this.guestSwiper?.update());
                this.guestResizeObserver.observe(el.nativeElement);
            });
        }
    }

    private getGuestSwiperOptions(): SwiperOptions {
        const guestCount = this.displayEventGuestsOfHonor.length;
        return {
            loop: guestCount > 3,
            observer: true,
            observeParents: true,
            spaceBetween: 20,
            speed: 1500,
            autoplay: {
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            },
            breakpoints: {
                0: {slidesPerView: 1, spaceBetween: 10},
                768: {slidesPerView: 2, spaceBetween: 20},
                992: {slidesPerView: 3, spaceBetween: 20}
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true
            }
        };
    }

    @ViewChild('speakerSwiperContainer') set speakerSwiperContainer(el: ElementRef<HTMLDivElement> | undefined) {
        if (el && !this.speakerSwiper) {
            setTimeout(() => {
                this.speakerSwiper = new Swiper(el.nativeElement, this.getSpeakerSwiperOptions());
                this.speakerResizeObserver = new ResizeObserver(() => this.speakerSwiper?.update());
                this.speakerResizeObserver.observe(el.nativeElement);
            });
        }
    }

    private getSpeakerSwiperOptions(): SwiperOptions {
        const speakerCount = this.displayEventSpeakers.length;
        return {
            loop: speakerCount > 3,
            observer: true,
            observeParents: true,
            spaceBetween: 20,
            speed: 1500,
            autoplay: {
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            },
            breakpoints: {
                0: {slidesPerView: 1, spaceBetween: 10},
                768: {slidesPerView: 2, spaceBetween: 20},
                992: {slidesPerView: 3, spaceBetween: 20}
            },
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true
            }
        };
    }

    getInitials(name: string | undefined): string {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        const initials = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2);
        return initials.toUpperCase();
    }
}
